import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import {
  rblProvidersFixture,
  rblIpsFixture,
  rblStatusFixture,
} from './fixtureRbl';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-rbl-config.md
 * Endpoints: /v1/admin/rbl/*
 *
 * Nexus seeded vier Default-Provider (Spamhaus ZEN, Barracuda, SpamCop,
 * SORBS) bei Migration 0004 — Orbit braucht keine Initial-Liste.
 */

export function useRblProviders() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-rbl-providers', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return rblProvidersFixture();
      }
      const res = await nexusFetch('/v1/admin/rbl/providers');
      const list = res?.providers ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { providers: list, raw: res };
    },
    staleTime: 60_000,
  });
}

export function useUpsertRblProvider() {
  return useApiMutation({
    live: (_fetch, { identifier, ...body }) =>
      nexusFetch(`/v1/admin/rbl/providers/${encodeURIComponent(identifier)}`, {
        method: 'PUT',
        body,
      }),
    fixture: ({ identifier }) => ({ ok: true, identifier }),
    invalidate: ['nexus-rbl-providers'],
    successMessage: (_res, { identifier }) => `Provider ${identifier} gespeichert.`,
  });
}

export function useDeleteRblProvider() {
  return useApiMutation({
    live: (_fetch, { identifier }) =>
      nexusFetch(`/v1/admin/rbl/providers/${encodeURIComponent(identifier)}`, {
        method: 'DELETE',
      }),
    fixture: ({ identifier }) => ({ ok: true, identifier }),
    invalidate: ['nexus-rbl-providers'],
    successMessage: (_res, { identifier }) => `Provider ${identifier} entfernt.`,
  });
}

export function useRblIps() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-rbl-ips', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return rblIpsFixture();
      }
      const res = await nexusFetch('/v1/admin/rbl/ips');
      const list = res?.ips ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { ips: list, raw: res };
    },
    staleTime: 30_000,
  });
}

export function useUpsertRblIp() {
  return useApiMutation({
    live: (_fetch, { ip, ...body }) =>
      nexusFetch(`/v1/admin/rbl/ips/${encodeURIComponent(ip)}`, {
        method: 'PUT',
        body,
      }),
    fixture: ({ ip }) => ({ ok: true, ip }),
    invalidate: ['nexus-rbl-ips'],
    successMessage: (_res, { ip }) => `IP ${ip} gespeichert.`,
  });
}

export function useDeleteRblIp() {
  return useApiMutation({
    live: (_fetch, { ip }) =>
      nexusFetch(`/v1/admin/rbl/ips/${encodeURIComponent(ip)}`, { method: 'DELETE' }),
    fixture: ({ ip }) => ({ ok: true, ip }),
    invalidate: ['nexus-rbl-ips'],
    successMessage: (_res, { ip }) => `IP ${ip} entfernt.`,
  });
}

/**
 * GET /v1/admin/rbl/status — gruppiert nach IP, jede Zeile mit `listings[]`.
 * Polling alle 30 s. Nexus-Cron läuft stündlich; Polling spiegelt also
 * Daten-Stand, nicht aktive Scans.
 *
 * Listing-Drift (Launch-Briefing 2026-06-14): Backend liefert
 * `classification`, `detected_at`, `last_seen_at`. Frühere Snapshots
 * nutzten `return_label`, `first_seen_at`, `last_checked_at`. Wir
 * normalisieren auf die historischen UI-Namen damit die View stabil
 * bleibt und ergänzen die neuen Namen als Fallback.
 */
export function useRblStatus() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-rbl-status', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      const res = fixturesMode
        ? (await fixtureDelay(), rblStatusFixture())
        : await nexusFetch('/v1/admin/rbl/status');
      const raw = res?.status ?? res?.items ?? (Array.isArray(res) ? res : []);
      const rows = raw.map((r) => ({
        ...r,
        clean: r.clean ?? (r.listings?.length ?? 0) === 0,
        listings: (r.listings ?? []).map(normalizeListing),
      }));
      return { rows, raw: res };
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

function normalizeListing(l) {
  if (!l || typeof l !== 'object') return l;
  return {
    ...l,
    provider_name: l.provider_name ?? l.providerName ?? l.provider ?? null,
    dns_zone: l.dns_zone ?? l.dnsZone ?? null,
    return_code: l.return_code ?? l.returnCode ?? null,
    return_label: l.return_label ?? l.classification ?? null,
    first_seen_at: l.first_seen_at ?? l.detected_at ?? l.firstSeenAt ?? null,
    last_checked_at:
      l.last_checked_at ?? l.last_seen_at ?? l.lastCheckedAt ?? null,
    lookup_url: l.lookup_url ?? l.lookupUrl ?? null,
  };
}

export function useTriggerRblScan() {
  return useApiMutation({
    live: (_fetch, { ip }) =>
      nexusFetch('/v1/admin/rbl/scan', {
        method: 'POST',
        body: { ip_address: ip },
      }),
    fixture: ({ ip }) => ({ ok: true, ip, job_id: `rbl-scan-${Date.now()}` }),
    invalidate: ['nexus-rbl-status'],
    successMessage: (_res, { ip }) => `RBL-Scan für ${ip} angestoßen.`,
  });
}
